import React, { useRef } from 'react';
import { Box, Button, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandMore, Visibility } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

// One channel's card in the send summary (e-mail and WhatsApp). Essentials visible; the rest behind
// "פרטים נוספים", whose open state belongs to the dialog. No physical left/right here (jss-rtl flips them).

const useStyles = makeStyles((theme) => ({
    // Two cards per row while each gets 320px, stacked below that.
    card: {
        flex: '1 1 320px', minWidth: 0, boxSizing: 'border-box',
        border: '1px solid var(--pls-d-border, #e0e0e0)', borderRadius: 8, background: 'var(--pls-d-surface, #fff)',
        padding: theme.spacing(2, 2.5),
        display: 'flex', flexDirection: 'column',
    },
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1), flexWrap: 'wrap' },
    title: { fontSize: 17, fontWeight: 700, color: 'var(--pls-d-text, rgba(0, 0, 0, 0.87))', margin: 0 },
    countRow: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
    count: { fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: 'var(--pls-d-success, #2e7d32)' },
    countLabel: { fontSize: 16, fontWeight: 600, color: 'var(--pls-d-text-2, #42526b)' },
    shortfall: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: theme.spacing(0.5), marginTop: theme.spacing(0.5), color: (theme.palette.type === 'dark' ? (theme as any).custom.textSecondary : theme.palette.text.secondary) },
    linkBtn: { padding: 0, minWidth: 0, fontSize: 'inherit', verticalAlign: 'baseline', textDecoration: 'underline' },
    notices: { marginTop: theme.spacing(1.5) },
    toggle: { alignSelf: 'flex-start', marginTop: theme.spacing(1), padding: theme.spacing(0.5, 0.5) },
    details: { marginTop: theme.spacing(0.5) },
    line: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(2), padding: theme.spacing(0.75, 0), borderBottom: '1px dashed var(--pls-d-border, #eee)' },
    lineLabel: { flex: '0 0 auto', maxWidth: '45%', color: (theme.palette.type === 'dark' ? (theme as any).custom.textSecondary : theme.palette.text.secondary), fontWeight: 600 },
    // Alignment on the span, not on the <bdi> (dir=auto would align each value by its own script).
    lineValue: { flex: '1 1 auto', minWidth: 0, textAlign: 'end', overflowWrap: 'anywhere', color: 'var(--pls-d-text, rgba(0, 0, 0, 0.87))' },
}));

export interface SummaryDetail {
    key: string;
    label: string;
    value: React.ReactNode;
}

interface Props {
    /** DOM id prefix for aria. */
    id: string;
    title: string;
    /** No handler ⇒ no preview button. */
    onPreview?: (() => void) | null;
    previewDisabled?: boolean;
    /** The big number; `null` ⇒ no count row (see `status`). */
    count?: number | null;
    countLabel?: string;
    /** Shown where the count would be (spinner / error banner). */
    status?: React.ReactNode;
    /** One visible line under the count; its "פרטים" opens the details. */
    shortfall?: string | null;
    details: SummaryDetail[];
    expanded: boolean;
    onToggle: () => void;
    /** Warnings — always visible. */
    children?: React.ReactNode;
}

const SummaryCard: React.FC<Props> = ({
    id, title, onPreview, previewDisabled, count, countLabel, status, shortfall, details, expanded, onToggle, children,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    // The "פרטים" link unmounts once expanded, so it hands focus to this button first.
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const titleId = `${id}-summary-card-title`;
    const detailsId = `${id}-summary-card-details`;

    return (
        <Box component="section" className={classes.card} aria-labelledby={titleId}>
            <Box className={classes.head}>
                <Typography component="h3" id={titleId} className={classes.title}>{title}</Typography>
                {onPreview && (
                    <Button size="small" variant="outlined" color="primary" startIcon={<Visibility />}
                        disabled={!!previewDisabled} onClick={onPreview}>
                        {t('DataSources.send.preview')}
                    </Button>
                )}
            </Box>

            {status}

            {count != null && (
                <Box className={classes.countRow}>
                    <span className={classes.count}>{count.toLocaleString()}</span>
                    {countLabel && <span className={classes.countLabel}>{countLabel}</span>}
                </Box>
            )}

            {shortfall && (
                <Typography component="div" variant="body1" className={classes.shortfall}>
                    <span>{shortfall}</span>
                    {!expanded && details.length > 0 && (
                        <>
                            <span aria-hidden="true">—</span>
                            <Button color="primary" className={classes.linkBtn}
                                onClick={() => { if (toggleRef.current) toggleRef.current.focus(); onToggle(); }}
                                aria-controls={detailsId} aria-expanded={false}>
                                {t('DataSources.send.summary.showDetails')}
                            </Button>
                        </>
                    )}
                </Typography>
            )}

            {/* toArray: [false, false] is truthy and would leave an empty box. */}
            {React.Children.toArray(children).length > 0 && <Box className={classes.notices}>{children}</Box>}

            {details.length > 0 && (
                <>
                    <Button ref={toggleRef} size="small" color="primary" className={classes.toggle} onClick={onToggle}
                        aria-expanded={expanded} aria-controls={detailsId}
                        endIcon={<ExpandMore style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}>
                        {t('DataSources.send.summary.moreDetails')}
                    </Button>
                    <Collapse in={expanded}>
                        <Box id={detailsId} className={classes.details}>
                            {details.map((d) => (
                                <Box key={d.key} className={classes.line}>
                                    <span className={classes.lineLabel}>{d.label}</span>
                                    <span className={classes.lineValue}><bdi>{d.value}</bdi></span>
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                </>
            )}
        </Box>
    );
};

export default SummaryCard;
